import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, Users, Cat, Receipt, Shield, Clock, BookOpen, ChevronDown } from 'lucide-react';
import { APP_VERSION } from '../version';

export const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: Calendar, title: 'Planning intelligent', description: 'Gérez vos cours, stages et compétitions en un coup d\'œil' },
    { icon: Users, title: 'Gestion des cavaliers', description: 'Suivez les profils, licences et forfaits de vos membres' },
    { icon: Cat, title: 'Suivi de la cavalerie', description: 'Pensions, soins vétérinaires et demi-pensions' },
    { icon: Receipt, title: 'Facturation automatisée', description: 'Générez vos factures et suivez les paiements' },
    { icon: Shield, title: 'Sécurisé', description: 'Vos données sont protégées et conformes RGPD' },
    { icon: Clock, title: 'Gain de temps', description: 'Automatisez les tâches administratives récurrentes' },
  ];

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFC]">
      {/* Header - Empty, just for scroll effect */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-sm border-b border-stone-200 shadow-sm py-2' 
            : 'bg-transparent py-3'
        }`} 
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Empty header */}
      </header>

      {/* Hero - Full screen with large centered logo - solid color background */}
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden bg-[#2E4A3D]">
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center text-center px-4 animate-fadeIn">
          {/* Large Logo SVG - no background */}
          <div className="mb-4 sm:mb-6">
            <img 
              src="/logo-equibook.svg" 
              alt="Equibook" 
              className="w-56 h-56 sm:w-72 sm:h-72 md:w-96 md:h-96 object-contain"
              style={{ filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.3))' }}
            />
          </div>
          
          {/* Brand name - justified to logo width */}
          <h1 
            className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-2 w-56 sm:w-72 md:w-96 flex justify-between"
          >
            <span>E</span><span>q</span><span>u</span><span>i</span><span>b</span><span>o</span><span>o</span><span>k</span>
          </h1>
          
          {/* Tagline */}
          <p className="text-base sm:text-lg md:text-xl text-white/80 font-light max-w-xs sm:max-w-sm">
            Gestion de club équestre simplifiée
          </p>
          
          {/* Version */}
          <span className="mt-2 text-xs text-white/40">v{APP_VERSION}</span>
          
          {/* CTA Buttons - Se connecter & Créer un compte */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button 
              onClick={() => navigate('/login')}
              data-testid="hero-login"
              className="bg-white text-[#2E4A3D] hover:bg-white/90 text-base px-6 sm:px-8 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Se connecter
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/register')}
              data-testid="hero-register"
              className="border-2 border-white text-white hover:bg-white/10 text-base px-6 sm:px-8 py-2.5 rounded-full transition-all"
            >
              Créer un compte
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <button 
          onClick={scrollToContent}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white transition-colors animate-bounce"
          aria-label="Défiler vers le bas"
        >
          <ChevronDown className="w-8 h-8 sm:w-10 sm:h-10" />
        </button>
      </section>

      {/* Promotional Content on white background */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="animate-fadeIn">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-[#2E4A3D] leading-tight">
                Gérez votre club équestre en toute simplicité
              </h2>
              <p className="mt-4 sm:mt-6 text-base sm:text-lg text-stone-600 leading-relaxed">
                Equibook centralise la gestion de vos cavaliers, chevaux, cours et facturation. 
                Une solution moderne et intuitive pour les clubs équestres.
              </p>
              <div className="mt-6 sm:mt-8 flex flex-wrap gap-3 sm:gap-4">
                <Button 
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-2"
                  className="btn-action text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3"
                >
                  Commencer gratuitement
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/guide-public')}
                  className="btn-outline text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3"
                >
                  Découvrir
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/guide-public')}
                  className="btn-outline text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3"
                >
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Guide
                </Button>
              </div>
            </div>
            
            <div className="relative animate-fadeIn stagger-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxob3JzZXxlbnwwfHx8fDE3Mzk3OTc3OTJ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Équitation professionnelle"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-white rounded-xl shadow-lg p-3 sm:p-4 animate-fadeIn stagger-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <Cat className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-bold text-[#2E4A3D]">150+</p>
                    <p className="text-xs sm:text-sm text-stone-500">Clubs équipés</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-20 px-4 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#2E4A3D]">
              Tout ce dont vous avez besoin
            </h3>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-stone-600 max-w-2xl mx-auto">
              Une plateforme complète pour simplifier la gestion quotidienne de votre club
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={feature.title}
                  className="card-base card-hover animate-fadeIn"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#2E4A3D]/10 flex items-center justify-center mb-3 sm:mb-4">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#2E4A3D]" />
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#2E4A3D] mb-1 sm:mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-sm sm:text-base text-stone-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-[#2E4A3D]">
            Prêt à moderniser votre club ?
          </h3>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-stone-600">
            Rejoignez les clubs qui ont choisi Equibook pour leur gestion quotidienne
          </p>
          <div className="mt-6 sm:mt-8 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Button 
              onClick={() => navigate('/register')}
              className="btn-action text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3"
              data-testid="cta-register"
            >
              Créer mon compte
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/guide-public')}
              className="btn-outline text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3"
              data-testid="cta-guide"
            >
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Voir le guide
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2E4A3D] text-white py-10 sm:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-equibook.png" 
                alt="Equibook" 
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
              <div>
                <h2 className="text-xl sm:text-2xl font-serif font-bold">Equibook</h2>
                <p className="text-white/60 text-sm mt-0.5 sm:mt-1">Gestion équestre simplifiée</p>
              </div>
            </div>
            <p className="text-white/60 text-xs sm:text-sm">
              © {new Date().getFullYear()} Equibook. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
